
import Vue from 'vue';
import { ChartData, ChartOptions, ChartTooltipItem } from 'chart.js';

import BarChart from '@/components/charts/BarChart.vue';
import TreeView from '@/components/dashboard/TreeView.vue';
import ToolTip from '@/components/common/ToolTip.vue';
import EnergyGraphModal from '@/components/modal/EnergyGraphModal.vue';
import {
	CalculatedEnergy,
	EnergyModalParams,
	ModifyZoneEnergy,
	RealEnergyEdit,
	RealEnergyMonthly,
} from '@/interfaces/energy';
import { LocationTree, TreeBadge } from '@/interfaces/locationTree';
import { Device, Floor } from '@/interfaces/locations';
import { Room, Zone } from '@/interfaces/lighting';
import { DeviceType } from '@/interfaces/types';
import permissions from '@/lib/permissions';
import { AxiosError } from 'axios';

export default Vue.extend({
	components: {
		BarChart,
		TreeView,
		ToolTip,
		EnergyGraphModal,
	},

	data(): {
		loadData: boolean;
		months: number;
		dataCollection: ChartData;
		graphOptions: ChartOptions;
		energyModalParams: EnergyModalParams;
	} {
		return {
			loadData: false,
			months: 5,
			dataCollection: { labels: [], datasets: [] },
			graphOptions: {
				responsive: true,
				maintainAspectRatio: false,
				legend: {
					labels: {
						fontColor: '#fff',
					},
				},
				scales: {
					yAxes: [
						{
							scaleLabel: {
								labelString: 'kWh',
								display: true,
								fontColor: 'fff',
							},
							gridLines: {
								color: '#0c1226',
							},
							ticks: {
								fontColor: '#fff',
							},
						},
					],
					xAxes: [
						{
							gridLines: {
								color: '#0c1226',
							},
							ticks: {
								fontColor: '#fff',
							},
						},
					],
				},
				tooltips: {
					callbacks: {
						label(tooltipItem: ChartTooltipItem, data: ChartData) {
							const start = data.datasets?.[tooltipItem.datasetIndex as number]?.label ?? '';

							let value: string;
							if (typeof tooltipItem.yLabel === 'number') {
								value = tooltipItem.yLabel.toFixed(2);
							} else {
								value = tooltipItem.yLabel!;
							}

							if (start) {
								return `${start}: ${value} kWh`;
							} else {
								return `${value} kWh`;
							}
						},
					},
				},
			},
			energyModalParams: {
				location: undefined,
				paramToggle: false,
				energyParams: undefined,
				energyGraph: undefined,
				extraInfo: {
					luminairesTotal: 0,
					maxPower: 0,
				},
			},
		};
	},

	async created() {
		await this.initialization();
	},

	computed: {
		zones(): Zone[] {
			return this.$store.getters['locations/zones/getZones'];
		},

		userCanReadRoom(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rooms',
				'read',
			]);
		},

		userCanWriteRoom(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rooms',
				'write',
			]);
		},

		realEnergies(): RealEnergyMonthly[] {
			return this.$store.getters['energy/energy/getRealEnergies'];
		},

		calculatedEnergies(): CalculatedEnergy[] {
			return this.$store.getters['energy/energy/getCalculatedEnergies'];
		},

		globalRefresh(): boolean {
			return this.$store.getters['tools/loading/getGlobalRefresh'];
		},

		treeEnergyData(): LocationTree[] {
			if (this.$store.getters['locations/floors/getFloors'].length !== 0) {
				return this.$store.getters['locations/floors/getSiteFloors'](
					this.$store.getters['tenancy/sites/getSelectedSite'].id
				)
					.sort((floorA: Floor, floorB: Floor) => {
						const nameA = floorA.name.toUpperCase();
						const nameB = floorB.name.toUpperCase();
						if (nameA < nameB) {
							return -1;
						}
						if (nameA > nameB) {
							return 1;
						}
						return 0;
					})
					.map((floorNode: Floor) => {
						const nodes: LocationTree[] = this.$store.getters['locations/rooms/getFloorRooms'](
							floorNode.id
						)
							.sort((roomA: Room, roomB: Room) => {
								const nameA = roomA.name.toUpperCase();
								const nameB = roomB.name.toUpperCase();
								if (nameA < nameB) {
									return -1;
								}
								if (nameA > nameB) {
									return 1;
								}
								return 0;
							})
							.map(this.getFloorChild);
						return {
							...floorNode,
							nodes: nodes,
						};
					});
			} else {
				return [];
			}
		},

		chartData(): ChartData {
			const chartLabels: string[] = [];
			const totalEnergySet: number[] = [];
			const projectedSet: number[] = [];
			const previousConsumptionSet: number[] = [];

			const previousConsumption: number = this.zones.reduce((total, zone) => {
				return (total += zone.previous_energy_consumption ?? 0);
			}, 0);
			const projected: number = this.calculatedEnergies.reduce((total, energy) => {
				return (total += energy.calculated_energy ?? 0);
			}, 0);

			for (let month = this.months - 1; 0 <= month; month--) {
				chartLabels.push(
					month === 0
						? this.$i18n.t('months.current_month').toString()
						: this.$i18n
								.tc('months.previous_month', Math.min(2, month), { count: month })
								.toString()
				);
				totalEnergySet.push(this.sumTotalEnergy(month));
				projectedSet.push(projected);
				previousConsumptionSet.push(previousConsumption);
			}

			return {
				labels: chartLabels,
				datasets: [
					{
						label: this.$i18n.t('Previous consumption').toString(),
						fill: false,
						borderColor: '#921c53',
						data: previousConsumptionSet,
						type: 'line',
						order: 1,
					},
					{
						label: this.$i18n.t('Projected consumption').toString(),
						fill: false,
						borderColor: '#cf7430',
						data: projectedSet,
						type: 'line',
						order: 2,
					},
					{
						label: this.$i18n.t('Actual consumption').toString(),
						backgroundColor: '#44a26a',
						data: totalEnergySet,
						order: 3,
					},
				],
			};
		},
	},

	watch: {
		globalRefresh: {
			async handler(refresh: boolean): Promise<void> {
				if (!refresh) {
					await this.initialization();
				}
			},
		},
	},

	methods: {
		async initialization(): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			this.loadData = true;
			await this.fetchEnergyData();
			this.loadData = false;
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async fetchEnergyData(): Promise<void> {
			const today = new Date();

			const promises: Promise<void>[] = [];
			let periods: RealEnergyEdit[] = [];
			for (let month = 0; month < this.months; month++) {
				periods.push({
					start_date: new Date(today.getFullYear(), today.getMonth() - month, 1).toISOString(),
					end_date: new Date(today.getFullYear(), today.getMonth() + 1 - month, 1).toISOString(),
					month: String(month),
				});
			}
			promises.push(
				this.$store.dispatch('energy/energy/fetchRealEnergy', {
					payload: periods,
					zones: this.zones,
				} as {
					payload: RealEnergyEdit[];
				})
			);
			promises.push(
				this.$store.dispatch('energy/energy/fetchCalculatedEnergy', {
					zones: this.zones,
				})
			);
			try {
				await Promise.all(promises);
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-fetch-energy-data',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
		},

		getFloorChild(roomNode: Room): LocationTree {
			const nodes: LocationTree[] = this.$store.getters['locations/zones/getRoomZones'](roomNode.id)
				.sort((zoneA: Zone, zoneB: Zone) => {
					const nameA = zoneA.name.toUpperCase();
					const nameB = zoneB.name.toUpperCase();
					if (nameA < nameB) {
						return -1;
					}
					if (nameA > nameB) {
						return 1;
					}
					return 0;
				})
				.map(this.getRoomChild);

			return {
				...roomNode,
				expanded: false,
				buttons: [
					{ buttonName: this.$t('Detail').toString(), buttonColor: 'info', category: 'rooms' },
				],
				category: 'Energy room',
				nodes: nodes,
			};
		},

		getRoomChild(zoneNode: Zone): LocationTree {
			const badges: TreeBadge[] = [];
			const realEnergy =
				this.realEnergies[0] && this.realEnergies[0][zoneNode.id]
					? this.realEnergies[0][zoneNode.id].real_energy
					: 0;
			if (realEnergy || realEnergy === 0) {
				badges.push({
					badgeName: `${String(realEnergy)} kWh`,
					badgeColor: 'danger',
					category: 'totalEnergy',
				});
			}

			return {
				...zoneNode,
				expanded: false,
				buttons: [
					{ buttonName: this.$t('Detail').toString(), buttonColor: 'info', category: 'zones' },
				],
				badges: badges,
				category: 'Energy zone',
			};
		},

		showDetail(category: string, deviceName: string, parentId: number): void {
			switch (category) {
				case 'rooms': {
					const room: Room = this.$store.getters['locations/rooms/getRoom'](parentId);
					const roomZones: Zone[] = this.$store.getters['locations/zones/getZones'].filter(
						(zone: Zone) => Number(zone.fk_room) === room.id
					);

					const powerTotal: number = roomZones.reduce((roomPowerTotal: number, zone: Zone) => {
						return (roomPowerTotal += this.sumTotalPower(zone));
					}, 0);

					this.energyModalParams = {
						location: room,
						paramToggle: false,
						energyGraph: {
							calculated_energy: roomZones.reduce((total, zone) => {
								return (total +=
									this.calculatedEnergies.find((energy) => energy.zone_id === zone.id)
										?.calculated_energy ?? 0);
							}, 0),
							real_energy: roomZones.reduce((total, zone) => {
								return (total += this.realEnergies[0][zone.id].real_energy ?? 0);
							}, 0),
						},
						energyParams: {
							expected_working_hours: roomZones.reduce((total, zone) => {
								return (total += zone.expected_working_hours ?? 0);
							}, 0),
							previous_energy_consumption: roomZones.reduce((total, zone) => {
								return (total += zone.previous_energy_consumption ?? 0);
							}, 0),
						},
						extraInfo: {
							luminairesTotal: roomZones.reduce((luminairesTotal: number, zone: Zone) => {
								return (luminairesTotal += zone.devices.length);
							}, 0),
							maxPower: powerTotal,
						},
					};
					break;
				}
				case 'zones': {
					const zone: Zone = this.$store.getters['locations/zones/getZone'](parentId);

					this.energyModalParams = {
						location: zone,
						paramToggle: true,
						energyGraph: {
							calculated_energy:
								this.calculatedEnergies.find((energy) => {
									return energy.zone_id === zone.id;
								})?.calculated_energy ?? 0,
							real_energy: this.realEnergies[0][zone.id].real_energy ?? 0,
						},
						energyParams: {
							expected_working_hours: zone.expected_working_hours || 0,
							previous_energy_consumption: zone.previous_energy_consumption || 0,
						},
						extraInfo: {
							luminairesTotal: this.$store.getters['devices/luminaires/getLuminaires'].filter(
								(luminaire: Device) => zone.devices.includes(luminaire.id)
							).length,
							maxPower: this.sumTotalPower(zone),
						},
					};
					break;
				}
			}

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.energyGraphModal as any).show();
		},

		cleanModalData(): void {
			this.energyModalParams = {
				location: undefined,
				paramToggle: false,
				energyGraph: undefined,
				energyParams: undefined,
				extraInfo: {
					luminairesTotal: null,
					maxPower: null,
				},
			};
		},

		sumTotalEnergy(month: number): number {
			return this.zones.reduce((total, zone) => {
				return (total += this.realEnergies[month]
					? (this.realEnergies[month][zone.id] && this.realEnergies[month][zone.id].real_energy) ??
					  0
					: 0);
			}, 0);
		},

		sumTotalPower(zone: Zone): number {
			return zone.devices.reduce((powerTotal: number, deviceId: number) => {
				const device: Device = this.$store.getters['devices/luminaires/getLuminaire'](deviceId);

				if (!device || !device.fk_device_type) {
					return powerTotal;
				}

				const deviceType: DeviceType = this.$store.getters['types/deviceTypes/getDeviceType'](
					device.fk_device_type
				);

				if (!deviceType || !deviceType.rated_power) {
					return powerTotal;
				}

				return (powerTotal += deviceType.rated_power);
			}, 0);
		},

		async modifyEnergy(zoneEnergyParams: ModifyZoneEnergy): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('locations/zones/modifyZone', {
					id: zoneEnergyParams.id,
					payload: {
						expected_working_hours: zoneEnergyParams.expected_working_hours,
						previous_energy_consumption: zoneEnergyParams.previous_energy_consumption,
					},
				});
				await this.fetchEnergyData();
				const zone: Zone = this.$store.getters['locations/zones/getZone'](zoneEnergyParams.id);
				this.energyModalParams.energyGraph = {
					calculated_energy:
						this.calculatedEnergies.find((energy) => {
							return energy.zone_id === zone.id;
						})?.calculated_energy ?? 0,
					real_energy: this.realEnergies[0][zone.id].real_energy ?? 0,
				};
				this.energyModalParams.energyParams = {
					expected_working_hours: zone.expected_working_hours,
					previous_energy_consumption: zone.previous_energy_consumption,
				};
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-modify-energy',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},
	},
});
